import type {
  DesignSystemRef,
  ShellFramePlan,
  ShellFrameTemplatePlan,
  ShellInjectionRef,
  SurfaceDecision,
} from '../kernel-contract.js';
import type { ShellRuntime } from '../surfaces/shell-frame.js';
import type { InstallClient } from '../install/install-client.js';
import { catalogEntry, STANDARD_CATALOG } from '../install/catalog.js';

/**
 * DEMO decision source — the dev/verification stand-in for the kernel boot/auth API.
 *
 * In production the host fetches a {@link SurfaceDecision} from the kernel's boot endpoint (a
 * thin same-origin call) and renders it. There is no kernel API to call in this scaffold, so
 * this module fabricates a decision from URL params purely so the host can be stood up and
 * driven — `?surface=sign-in|empty-workspace|error|hand-to-shell` and `?brand=alt` to prove
 * a token swap re-skins every `@caos/ui` component (AC-2). It is NOT kernel logic: the kernel
 * owns the real resolution order; this only shapes fixture data the host consumes as-is.
 *
 * The DEMO fixtures below stand in for the kernel's emitted `DesignSystemRef`; the default's
 * brand seam mirrors the kernel's baked default (`runtime/design-system.ts BAKED_DEFAULT_TOKENS`).
 */

/**
 * The kernel's baked-default design system as a fixture (mirrors `BAKED_DEFAULT_TOKENS`). In
 * production this ref arrives inside the `SurfaceDecision` from the kernel, not built here.
 */
const DEFAULT_DESIGN_SYSTEM: DesignSystemRef = {
  key: 'caos-default',
  source: 'default',
  version: 0,
  tokens: {
    '--brand-accent': '#00969B',
    '--brand-accent-strong': '#016E75',
    '--brand-accent-soft': '#E4F4F4',
    '--brand-mark': '"◆"',
    '--brand-wordmark': '"CAOS"',
    '--brand-os-suffix': '"CLOUD ATLANTIS OS"',
    '--brand-ai-app-name': '"Atlantis AI"',
  },
};

/**
 * A generic, anonymous stand-in white-label override (design-system/tokens/acme.brand.tokens.json)
 * as an installed {@link DesignSystemRef}, used to demonstrate the token swap. It carries no real
 * customer's identity — its sole job is to prove the platform is white-label. Only the brand seam
 * changes; every `--caos-*-accent*` derives from `--brand-accent`, so this one small map repaints
 * every accent across the surface.
 */
const ALT_DESIGN_SYSTEM: DesignSystemRef = {
  key: 'acme',
  source: 'installed',
  version: 1,
  tokens: {
    '--brand-accent': '#6B4FBB',
    '--brand-accent-strong': '#543E96',
    '--brand-accent-soft': '#ECE7F8',
    '--brand-wordmark': '"ACME OS"',
    '--brand-ai-app-name': '"ACME AI"',
  },
};

/**
 * The `caos_base` theme (from the standard `pkg_core_shell` package) as an installed
 * {@link DesignSystemRef} — the theme the shell frame stamps in production. Used as the frame's
 * design in the `hand-to-shell` fixture so the assembled chrome brands from real shell tokens.
 */
const CAOS_BASE_DESIGN_SYSTEM: DesignSystemRef = {
  key: 'caos_base',
  source: 'installed',
  version: 1,
  tokens: {
    '--brand-accent': '#00969B',
    '--brand-accent-strong': '#016E75',
    '--brand-accent-soft': '#E4F4F4',
    '--brand-wordmark': '"CAOS"',
    '--caos-color-bg': '#F5F8F8',
    '--caos-color-surface': '#FFFFFF',
    '--caos-color-surface-2': '#EDF2F2',
    '--caos-color-accent': '#00969B',
    '--caos-color-text': '#0B1416',
    '--caos-color-text-muted': '#5C6B6E',
    '--caos-color-border': '#E2E8E9',
    '--caos-color-chrome': '#05101A',
    '--caos-color-chrome-text': '#E6F4F4',
  },
};

function designFor(url: URL): DesignSystemRef {
  return url.searchParams.get('brand') === 'alt' ? ALT_DESIGN_SYSTEM : DEFAULT_DESIGN_SYSTEM;
}

/**
 * CAOS-713 — the standard `workspace` blueprint (CAOS-658), as the kernel resolves it for an org
 * that has authored none of its own. In production this rides on the real `hand-to-shell` decision
 * (`runtime/boot-orchestrator.ts` `resolveWorkspaceFrame`); this fixture stands in for it here so
 * the demo exercises the SAME interpreted-region path production does, not the host's absent-plan
 * fallback.
 */
const DEMO_WORKSPACE_FRAME: ShellFrameTemplatePlan = {
  requested: 'workspace',
  resolved: true,
  regions: [
    { key: 'banner', label: 'Banner', pinned: true },
    { key: 'header', label: 'Header', pinned: true },
    { key: 'nav_strip', label: 'App Navigation', pinned: true },
    { key: 'record_tabs_strip', label: 'Record Tabs', pinned: true },
    { key: 'main', label: 'Main', columns: 2 },
  ],
  geometry: {
    areas: ['banner', 'header', 'nav_strip', 'record_tabs_strip', 'main'],
    cols: 'minmax(0, 1fr)',
    stack: ['banner', 'header', 'nav_strip', 'record_tabs_strip', 'main'],
  },
};

/**
 * A representative {@link ShellFramePlan} for the `hand-to-shell` fixture — the mastered shell,
 * the 5 required surfaces, and injections into every seam, so the assembled chrome exercises the
 * whole map. In production this arrives from the kernel's boot decision; here it stands in.
 */
function demoShellPlan(url: URL): ShellFramePlan {
  const design = url.searchParams.get('brand') === 'alt' ? ALT_DESIGN_SYSTEM : CAOS_BASE_DESIGN_SYSTEM;
  return {
    master: 'pkg_core_shell',
    surfaces: [
      'impersonation-banner',
      'global-header',
      'app-navigation-strip',
      'record-tabs-strip',
      'app-switcher',
    ],
    injections: {
      'navigation-entries': [
        {
          injectionPoint: 'navigation-entries',
          sourceKey: 'sales_atlas',
          label: 'Sales Atlas',
          landingTab: 'accounts',
          tabs: [
            { key: 'accounts', label: 'Accounts', object: 'account' },
            { key: 'contacts', label: 'Contacts', object: 'contact' },
            { key: 'orders', label: 'Orders', object: 'order' },
          ],
        },
        { injectionPoint: 'navigation-entries', sourceKey: 'operations', label: 'Operations' },
        { injectionPoint: 'navigation-entries', sourceKey: 'reports', label: 'Reports' },
      ],
      'header-actions': [{ injectionPoint: 'header-actions', sourceKey: 'global_search' }],
      'avatar-menu': [{ injectionPoint: 'avatar-menu', sourceKey: 'my_profile' }],
      // CAOS-450 — every edge-anchored injection now carries its full anchor declaration (edge,
      // DECLARED order, label, colour), the same SHAPE the real `pkg_atlantis_ai` /
      // `pkg_report_a_bug` / `pkg_guidance` capabilityClaims carry. The specific order/label/colour
      // values below are synthetic and chosen for this fixture's own purpose — they do not mirror
      // the packages' real current declarations (see host/src/install/catalog.ts for those, kept in
      // sync by hand). The `order` values here are deliberately chosen so this fixture proves the
      // stacking is NOT install/array order — Report a Bug is listed first in this array but
      // declares order 3, so it renders LAST.
      'edge-anchored': [
        {
          injectionPoint: 'edge-anchored',
          sourceKey: 'atlantis_ai',
          label: 'Atlantis AI',
          anchor: { edge: 'right', order: 1, label: 'AI', colour: '#00969B' },
        },
        // CAOS-320 — the Report-a-Bug orb is a REAL engine-rendered intake form (not a static
        // button): its injection carries the intake descriptor, so clicking it opens a working form
        // that writes a bug_report row via `/api/data/create`. Mirrors what `enrichShellPlan` attaches
        // from the org's installed metadata in production.
        {
          injectionPoint: 'edge-anchored',
          sourceKey: 'report_a_bug',
          label: 'Report a Bug',
          anchor: { edge: 'right', order: 3, label: 'Bug', colour: '#B3541E' },
          intake: {
            title: 'Report a Bug',
            write: { noun: 'data', verb: 'create' },
            object: 'bug_report',
            submitLabel: 'Send report',
            fields: [
              { field: 'bug_report.description', label: 'What went wrong?', control: 'textarea', required: true, placeholder: 'Describe what you were doing and what happened…' },
              {
                field: 'bug_report.kind',
                label: 'Kind',
                control: 'picklist',
                options: [
                  { value: 'bug', label: 'Bug' },
                  { value: 'confusing', label: 'Confusing' },
                  { value: 'idea', label: 'Idea' },
                ],
              },
              { field: 'bug_report.screenshot_urls', label: 'Screenshots', control: 'attachment' },
            ],
          },
        },
        // CAOS-148/66 — the built-in Guidance Center tab, alongside the assistant + report tabs.
        {
          injectionPoint: 'edge-anchored',
          sourceKey: 'guidance_home',
          label: 'Guidance Center',
          anchor: { edge: 'right', order: 2, label: 'Guide', colour: '#8A1EB3' },
        },
      ],
    },
    design,
    // CAOS-713 — carry the resolved workspace blueprint so the demo frame renders through the same
    // interpreted-region path production does.
    frame: DEMO_WORKSPACE_FRAME,
  };
}

/**
 * A representative {@link ShellRuntime} for the `hand-to-shell` fixture: the signed-in user, the
 * active app, and a resolved record content plan so the frame renders a populated app. `?record=0`
 * (or `list`/`fallback`) drives the content surface. Production wires this from the session + route.
 */
export function demoShellRuntime(url: URL): ShellRuntime {
  // CAOS-302/291 fixture views: `?view=home` → the Atlantis AI Home (no app pre-selected);
  // `?view=app` → open Sales Atlas on its landing tab (no explicit content plan, so the frame
  // derives the landing-tab surface — proving there is no fabricated "APP + cards" page).
  const view = url.searchParams.get('view');
  if (view === 'home') {
    return { user: { name: 'Riley Chen', sub: 'Operations · Contoso' }, env: 'SANDBOX' };
  }
  if (view === 'app') {
    return {
      user: { name: 'Riley Chen', sub: 'Operations · Contoso' },
      env: 'SANDBOX',
      activeAppKey: 'sales_atlas',
      activeAppLabel: 'Sales Atlas',
    };
  }

  const contentParam = url.searchParams.get('content');
  const content =
    contentParam === 'list'
      ? ({
          kind: 'list' as const,
          app: 'sales_atlas',
          tab: 'accounts',
          object: 'account',
          title: 'Accounts',
          columns: ['Name', 'Industry', 'Owner', 'Annual Revenue'],
          rows: [
            { Name: 'Northwind Traders', Industry: 'Retail', Owner: 'R. Chen', 'Annual Revenue': '$42M' },
            { Name: 'Fabrikam Freight', Industry: 'Transportation', Owner: 'J. Okafor', 'Annual Revenue': '$18M' },
            { Name: 'Contoso Clinics', Industry: 'Healthcare', Owner: 'M. Alvarez', 'Annual Revenue': '$7.5M' },
          ],
        } as const)
      : contentParam === 'fallback'
        ? ({
            kind: 'fallback' as const,
            fallbackKind: 'missing-record' as const,
            message: 'The record you followed no longer exists or was moved.',
          } as const)
        : ({
            kind: 'record' as const,
            app: 'sales_atlas',
            tab: 'accounts',
            object: 'account',
            recordId: 'acct_northwind',
            title: 'Northwind Traders',
            tabs: ['Details', 'Related', 'Orders', 'History'],
            sections: [
              { label: 'Account Information', fields: ['Account Name', 'Industry', 'Type', 'Owner'] },
              { label: 'Address', fields: ['Billing Street', 'Billing City', 'Billing State', 'Billing Postal Code'] },
            ],
          } as const);

  return {
    user: { name: 'Riley Chen', sub: 'Operations · Contoso' },
    env: 'SANDBOX',
    activeAppKey: 'sales_atlas',
    activeAppLabel: 'Sales Atlas',
    content,
  };
}

/** Build a fixture {@link SurfaceDecision} from the URL for the host to render. */
export function demoDecision(url: URL): SurfaceDecision {
  const surface = url.searchParams.get('surface') ?? 'sign-in';
  const design = designFor(url);

  switch (surface) {
    case 'empty-workspace': {
      // `?apps=1` (or `installed`) drives the app-aware empty state (CAOS-205): an app is
      // installed but no workspace exists yet. Absent ⇒ the truly-empty standing state.
      const appsParam = url.searchParams.get('apps');
      const appsInstalled = appsParam === '1' || appsParam === 'installed' || appsParam === 'true';
      return {
        surface: 'empty-workspace',
        org: { orgId: 'demo-org', host: url.host },
        design,
        actions: appsInstalled
          ? [{ id: 'install-shell', label: 'Install workspace' }]
          : [
              { id: 'install-shell', label: 'Install workspace' },
              { id: 'deploy-app', label: 'Deploy first app' },
            ],
        appsInstalled,
      };
    }

    case 'error':
      return {
        surface: 'error',
        stage: 'validate',
        issues: [
          {
            path: '/',
            code: 'component.invalid.required',
            message: 'A component failed validation during boot and the registry did not hydrate.',
            params: {},
          },
        ],
        design,
        // CAOS-662 — the envelope the Engine's boot normalizer emits for exactly this issue.
        error: {
          code: 'validation.bundle_component',
          class: 'validation',
          message: "Something installed in this organization failed its checks, so it can't open. An admin needs to fix or remove it.",
          fault: 'admin',
          retriable: false,
        },
      };

    case 'hand-to-shell':
      // CAOS-243: carry the frame plan so the host assembles the chrome (a bare decision would
      // stand aside). The runtime context (user / content) is supplied via `demoShellRuntime`.
      return { surface: 'hand-to-shell', shell: demoShellPlan(url) };

    case 'sign-in':
    default:
      return {
        surface: 'sign-in',
        auth: {
          password: true,
          sso: [{ id: 'entra', label: 'Continue with SSO' }],
        },
        design,
      };
  }
}

/* ── CAOS-211 fixture install wiring ─────────────────────────────────────────────────────────
 *
 * The empty-workspace CTAs install through the {@link InstallClient} boundary. In the dev fixture
 * there is no Engine to POST to, so this in-memory client records what was "installed" in a local
 * set, and {@link demoRebootDecision} reads that set to produce the honest post-install boot
 * decision — an app installed but no workspace stays the app-aware empty state; a workspace flips
 * to `hand-to-shell`. This lets Playwright drive the WHOLE UI flow (empty → picker → painted
 * shell, with a deployed app in the switcher) against `vite dev`, no kernel required.
 */

/** The fixture's "installed packages" — mutated by {@link demoInstallClient}. */
const demoInstallState = new Set<string>();

/** The in-memory install boundary for the dev fixture: record the install after a visible beat. */
export const demoInstallClient: InstallClient = {
  async install(packageId: string) {
    // A short, visible delay so the picker's progress state is observable in Playwright.
    await new Promise((resolve) => setTimeout(resolve, 350));
    demoInstallState.add(packageId);
    return { ok: true, version: demoInstallState.size };
  },

  /**
   * CAOS-1728 — the by-key door, for the proving catalog's fixture workspaces.
   *
   * It records the PACKAGE key, not the universal key it was called with, because
   * `demoRebootDecision` and the picker's `isInstalled` both read `demoInstallState` as a set of
   * package keys. Storing the `pvk_` would leave the fixture installed-but-invisible: the install
   * reports success, the reboot reads no new workspace, and the empty state stays up — which looks
   * exactly like a broken install.
   *
   * THE PACKAGE KEY IS A PARAMETER RATHER THAN A LOOKUP, and that is not a style choice. THIS
   * MODULE IS IN THE PRODUCTION BUNDLE: `main.ts` imports it unconditionally and only branches on
   * `useFixture` at runtime, so an `import.meta.env.DEV` guard elsewhere does nothing for anything
   * imported here. A first version of this resolved the package by holding a copy of the
   * key→package map, and a grep of the built bundle then found `pvk_` and `pkg_ws_slate` in the
   * shipped JavaScript — the fixture catalog's own strings, in a customer artifact, arriving
   * through the file meant to keep them out. The caller knows the package key; it passes it.
   *
   * A key not shaped like a minted one is REFUSED rather than recorded, so the fixture answers the
   * way the Engine does for a key it cannot resolve. It cannot check more than the shape without
   * the map this deliberately does not hold, and that is the right trade: a fixture that knows
   * less here is a fixture that cannot leak.
   */
  async installByKey(universalKey: string, packageId: string) {
    await new Promise((resolve) => setTimeout(resolve, 350));
    if (!universalKey.startsWith('pvk_')) {
      return {
        ok: false,
        code: 'not_found.published_version',
        error: `The fixture holds no published version for ${universalKey}.`,
      };
    }
    demoInstallState.add(packageId);
    return { ok: true, version: demoInstallState.size };
  },

  /**
   * CAOS-401 — what the fixture says a package requires.
   *
   * The fixture exists so the WHOLE flow can be driven without an Engine, and a requirement line
   * that only ever appears against a real kernel is one nobody can see, drive in Playwright, or
   * screenshot. So this stands in for the resolver the same way `install` stands in for the
   * install: the shape is the Engine's, and the answer is the fixture's.
   *
   * It is deliberately thin. The real answer is read from what each package DECLARES, which this
   * fixture has no access to — it holds a catalog of entries, not package bodies — so it encodes
   * the one rule this ticket is about (an app needs the workspace under it) rather than pretending
   * to a general resolution it cannot perform. What it does NOT fake is the half that makes the
   * line worth reading: whether the workspace is already there is read from the same
   * `demoInstallState` an install mutates, so installing the workspace and reopening the picker
   * genuinely changes the sentence, exactly as it would against a kernel.
   */
  async preview(packageId: string) {
    if (packageId === SHELL_PACKAGE || !demoAppPackages.has(packageId)) {
      return { ok: true, prerequisites: [] };
    }
    return {
      ok: true,
      prerequisites: [
        {
          package: SHELL_PACKAGE,
          range: '>=0.1.0',
          imposedBy: packageId,
          version: '0.1.0',
          status: demoInstallState.has(SHELL_PACKAGE)
            ? ('already-installed' as const)
            : ('will-install' as const),
        },
      ],
    };
  },
};

/** The workspace package every app in the catalog sits on top of (CAOS-401). */
const SHELL_PACKAGE = 'pkg_core_shell';

/**
 * The app packages the fixture answers for, derived from the catalog rather than listed, so an app
 * added to the shelf is covered without anyone remembering to add it here too.
 */
const demoAppPackages = new Set(
  STANDARD_CATALOG.filter((e) => e.kind === 'app').flatMap((e) => e.packages),
);

/**
 * CAOS-287 — what the fixture currently has installed, for the "Get more apps" surface.
 *
 * A READER over the same set {@link demoInstallClient} mutates, rather than a second copy: the
 * fixture's whole value is that installing something changes what the next screen shows, and a
 * snapshot taken alongside it would drift the moment anything was installed.
 */
export function demoInstalledPackageKeys(): ReadonlySet<string> {
  return demoInstallState;
}

/** The shell injections implied by the currently-installed app packages (from the catalog map). */
function installedInjections(): Record<string, ShellInjectionRef[]> {
  const injections: Record<string, ShellInjectionRef[]> = {};
  for (const id of demoInstallState) {
    const inj = catalogEntry(id)?.shellInjection;
    if (!inj) continue;
    (injections[inj.seam] ??= []).push({
      injectionPoint: inj.seam,
      sourceKey: inj.sourceKey,
      ...(inj.anchor ? { anchor: inj.anchor } : {}),
    });
  }
  return injections;
}

/**
 * The boot decision AFTER a fixture install, read from {@link demoInstallState}. No shell yet ⇒
 * the empty-workspace surface (app-aware once any app is installed); a shell ⇒ `hand-to-shell`
 * with a frame plan reflecting exactly the installed apps.
 */
export function demoRebootDecision(url: URL): SurfaceDecision {
  const hasShell = demoInstallState.has('pkg_core_shell');
  if (!hasShell) {
    const appsInstalled = [...demoInstallState].some((id) => catalogEntry(id)?.kind === 'app');
    return {
      surface: 'empty-workspace',
      org: { orgId: 'demo-org', host: url.host },
      design: designFor(url),
      actions: appsInstalled
        ? [{ id: 'install-shell', label: 'Install workspace' }]
        : [
            { id: 'install-shell', label: 'Install workspace' },
            { id: 'deploy-app', label: 'Deploy first app' },
          ],
      appsInstalled,
    };
  }
  return {
    surface: 'hand-to-shell',
    shell: {
      master: 'pkg_core_shell',
      surfaces: [
        'impersonation-banner',
        'global-header',
        'app-navigation-strip',
        'record-tabs-strip',
        'app-switcher',
      ],
      injections: installedInjections(),
      design: url.searchParams.get('brand') === 'alt' ? ALT_DESIGN_SYSTEM : CAOS_BASE_DESIGN_SYSTEM,
      frame: DEMO_WORKSPACE_FRAME,
    },
  };
}

/** The shell runtime AFTER a fixture install — active app + content reflect what was installed. */
export function demoShellRuntimeAfterInstall(url: URL): ShellRuntime {
  void url;
  const navKeys = [...demoInstallState]
    .map((id) => catalogEntry(id)?.shellInjection)
    .filter((inj): inj is NonNullable<typeof inj> => inj?.seam === 'navigation-entries')
    .map((inj) => inj.sourceKey);
  const salesInstalled = demoInstallState.has('pkg_sales_atlas');
  return {
    user: { name: 'Riley Chen', sub: 'Operations · Contoso' },
    env: 'SANDBOX',
    activeAppKey: navKeys[0],
    activeAppLabel: salesInstalled ? 'Sales Atlas' : undefined,
    content: salesInstalled
      ? {
          kind: 'list',
          app: 'sales_atlas',
          tab: 'accounts',
          object: 'account',
          title: 'Accounts',
          columns: ['Name', 'Industry', 'Owner', 'Annual Revenue'],
          rows: [
            { Name: 'Northwind Traders', Industry: 'Retail', Owner: 'R. Chen', 'Annual Revenue': '$42M' },
            { Name: 'Fabrikam Freight', Industry: 'Transportation', Owner: 'J. Okafor', 'Annual Revenue': '$18M' },
            { Name: 'Contoso Clinics', Industry: 'Healthcare', Owner: 'M. Alvarez', 'Annual Revenue': '$7.5M' },
          ],
        }
      : undefined,
  };
}
